import {
	Box,
	Button,
	createTheme,
	FormControlLabel,
	Grid,
	Modal,
	Stack,
	TextField,
	ThemeProvider,
	Typography,
} from '@mui/material';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { green, red } from '@mui/material/colors';
import {
	adminEnableOrDisableProfile,
	adminEnableOrDisableUser,
	adminProfileValidityExtend,
	adminUpdateProfile,
	adminViewProfile,
	adminGetUploadDataForProfile,
	adminMarkUploadedImageAsProfilePicture,
} from '../../services/admin';
import SelectInput from '../common/SelectInput';
import ProfileImage from '../profiles/ProfileImage';
import {
	annualIncomeOptions,
	bloodGroupOptions,
	casteOptions,
	countryOptions,
	drinkingHabitsOptions,
	smokingHabitsOptions,
	foodHabitsOptions,
	gotraOptions,
	kuldevataOptions,
	languageOptions,
	maritalStatusOptions,
	motherTongueOptions,
	nakshatraOptions,
	occupationOptions,
	qualificationOptions,
	rashiHindiOptions,
	stateOptions,
	mathOptions
} from '../constants/profileData';
import { Android12Switch } from '../common/Swtich';
import MultiselectInputChipbox from '../common/MultiselectInputChipbox';
import { modalStyle } from './AdminHome';
import { toast, ToastContainer } from 'react-toastify';
import ImageUploader from '../profiles/CreateProfile/ImageUploader';
import { 
	PROFILE_UPLOAD_IMAGE_LIMIT, 
	PROFILE_UPLOAD_STATUS_GETUPLOADDATA_FAILED, 
	PROFILE_UPLOAD_STATUS_NOT_SELECTED, 
	PROFILE_UPLOAD_STATUS_RESTORE_FAILED, 
	PROFILE_UPLOAD_STATUS_UPLOADED,
	UPLOAD_TIMEOUT,
	IMAGE_FETCH_TIMEOUT,
	IMAGE_RESTORE_PLACEHOLDER_MESSAGE,
	IMAGE_RESTORE_SUCCESS_MESSAGE,
	IMAGE_RESTORE_PARTIAL_SUCCESS_MESSAGE,
	IMAGE_RESTORE_FAILED_MESSAGE,
	PROFILE_UPLOAD_STATUS_UPLOADING
} from '../constants/Constants';
import { BACKEND_IMAGE_URL } from '../../services/Constants';
import SearchableSelectInput from '../common/SearchableSelectInput';

export const currentValidity = (currentValidity) => {
	let currentDate = new Date().getTime();
	let remainingValidityInMillis = parseFloat(currentValidity);
	let validityInMillis = currentDate + remainingValidityInMillis;
	let validityDate = new Date(validityInMillis);
	return validityDate.toDateString();
};

export const AdminViewProfile = () => {
	// const [pageLoaded, setPageLoaded] = React.useState(false);
	const [currentProfile, setCurrentProfile] = React.useState(undefined);
	const [currentUser, setCurrentUser] = React.useState(undefined);
	const [editProfileEnabled, setEditProfileEnabled] = React.useState(false);
	const [profileNeedsUpdate, setProfileNeedsUpdate] = React.useState(false);
	const [validityExtensionUser, setValidityExtensionUser] =
		React.useState(false);
	
	// Image upload state
	const [profileImageUploadUrls, setProfileImageUploadUrls] = React.useState([]);
	const [profileImageUploadStatuses, setProfileImageUploadStatuses] = React.useState(new Array(PROFILE_UPLOAD_IMAGE_LIMIT).fill(PROFILE_UPLOAD_STATUS_UPLOADING));
	const [localProfileImageUrls, setLocalProfileImageUrls] = React.useState([]);
	const [profileUploadDataKeys, setProfileUploadDataKeys] = React.useState([]);
	// Enforces image rerendering
	const [refreshCount, setRefreshCount] = React.useState(0);

	const { getToken } = useAuth();
	let { id } = useParams();
	const navigate = useNavigate();

	const theme = createTheme({
		palette: {
			green: {
				main: green[500],
			},
			red: {
				main: red[500],
			},
		},
	});

	React.useEffect(() => {
		refreshCurrentProfile();
	}, []);

	const fetchProfileUploadData = async (showRestoreMessage = false) => {
		try {
			let token = getToken()?.token;
			let response = await adminGetUploadDataForProfile(token, id);
			if (response.errors) {
				setProfileImageUploadStatuses(new Array(PROFILE_UPLOAD_IMAGE_LIMIT).fill(PROFILE_UPLOAD_STATUS_GETUPLOADDATA_FAILED));
				toast.error('Failed to get upload data for profile');
				return null;
			}
			const uploadData = response.data.adminGetUploadDataForProfile;
			
			// Initialize arrays for each upload data
			const urls = uploadData.map(data => data.url);
			const keys = uploadData.map(data => data.key);
			const statuses = new Array(uploadData.length).fill(PROFILE_UPLOAD_STATUS_NOT_SELECTED);
			const localUrls = new Array(uploadData.length).fill("");
			
			// If there are existing profile images, restore them in parallel
			if (currentProfile?.photoKeys?.length > 0) {
				// Show placeholder message
				if (showRestoreMessage) {
					toast.info(IMAGE_RESTORE_PLACEHOLDER_MESSAGE, {
						timeOut: 3000,
					});
				}
				
				const timestamp = new Date().getTime();
				
				// Create array of promises for parallel execution
				const restorePromises = currentProfile.photoKeys.slice(0, uploadData.length).map(async (photoKey, index) => {
					const existingImageUrl = `${BACKEND_IMAGE_URL}/images/profile?photoKey=${photoKey}&t=${timestamp}`;
					try {
						// Fetch the existing image
						const imageResponse = await fetch(existingImageUrl, {
							signal: AbortSignal.timeout(IMAGE_FETCH_TIMEOUT)
						});
						const imageBlob = await imageResponse.blob();
						
						// Upload to new path
						const uploadResponse = await fetch(urls[index], {
							method: 'PUT',
							body: imageBlob,
							headers: {
								'Content-Type': imageBlob.type
							},
							signal: AbortSignal.timeout(UPLOAD_TIMEOUT)
						});

						if(uploadResponse.status !== 200) {
							return {
								index,
								success: false,
								localUrl: existingImageUrl,
								status: PROFILE_UPLOAD_STATUS_RESTORE_FAILED
							};
						}
						
						return {
							index,
							success: true,
							localUrl: existingImageUrl,
							status: PROFILE_UPLOAD_STATUS_UPLOADED
						};
					} catch (error) {
						console.error('Error uploading existing image:', error);
						return {
							index,
							success: false,
							localUrl: existingImageUrl,
							status: PROFILE_UPLOAD_STATUS_RESTORE_FAILED
						};
					}
				});
				
				// Wait for all promises to resolve
				const results = await Promise.allSettled(restorePromises);
				
				// Process results
				let isPartialFailure = false;
				results.forEach((result) => {
					if (result.status === 'fulfilled') {
						const { index, success, localUrl, status } = result.value;
						localUrls[index] = localUrl;
						statuses[index] = status;
						if (!success) {
							isPartialFailure = true;
						}
					}
				});
				
				// Set remaining slots to NOT_SELECTED
				for (let i = currentProfile.photoKeys.length; i < uploadData.length; i++) {
					statuses[i] = PROFILE_UPLOAD_STATUS_NOT_SELECTED;
					localUrls[i] = "";
				}
				
				// Show appropriate message based on results
				if (isPartialFailure) {
					toast.warning(IMAGE_RESTORE_PARTIAL_SUCCESS_MESSAGE, {
						timeOut: 5000,
					});
				} else if (showRestoreMessage) {
					toast.success(IMAGE_RESTORE_SUCCESS_MESSAGE, {
						timeOut: 5000,
					});
				}
			}
			
			setProfileImageUploadUrls(urls);
			setProfileUploadDataKeys(keys);
			setProfileImageUploadStatuses(statuses);
			setLocalProfileImageUrls(localUrls);
			
			return urls;
		} catch(error) {
			toast.error(IMAGE_RESTORE_FAILED_MESSAGE, {
				timeOut: 3000,
			});
			return null;
		}
	};

	const refreshCurrentProfile = async () => {
		let listOfFilters = [];
		let profileFilter = `id: ${id}`;
		listOfFilters.push(profileFilter);
		let auth = getToken();
		if (auth) {
			try {
				adminViewProfile(auth.token, listOfFilters, 1, 1, '').then(
					(response) => {
						if (response.data.errors) {
							alert(
								'Issue in fetching profiles please try refreshing'
							);
							throw new Error(response.data.errors);
						} else {
							let entities = response.data.data.adminFilter.users;
							setCurrentProfile(entities[0].profile);
							setCurrentUser(entities[0].user);
							setRefreshCount(refreshCount + 1);
							
							// Initialize image upload data after profile is loaded
							fetchProfileUploadData(true);
						}
					}
				);
			} catch (error) {
				alert('There was an error in fetching the profiles');
				console.error('issue in View profile', error);
			}
		}
	};

	const saveUserPofile = async () => {
		let auth = getToken();
		if (auth) {
			try {
				let response = await adminUpdateProfile(
					auth.token,
					currentProfile
				);
				if (response.data.erros) {
					throw new Error(response.data.errrors);
				} else {
					setProfileNeedsUpdate(false);
				}
			} catch (error) {
				alert('There was an error in updating the profile');
				console.error('Error in Admin updating userProfile', error);
			}
		}
	};

	const extendValidity = () => {
		let auth = getToken();
		if (auth) {
			try {
				adminProfileValidityExtend(auth.token, currentProfile.id).then(
					(response) => {
						if (response.data.errors) {
							// setAllProfiles([]);
							throw new Error(response.data.errors);
						} else {
							toast.info(
								'Profile Validity extended by one year',
								{
									timeOut: 1000,
								}
							);
							setValidityExtensionUser(false);
							refreshCurrentProfile();
						}
					}
				);
			} catch (error) {
				console.error('Error in profile validity extension', error);
				alert(
					'Issue in extending profiles validity please try refreshing'
				);
			}
		}
	};

	const enableDisableProfile = () => {
		let auth = getToken();
		if (auth) {
			try {
				adminEnableOrDisableProfile(
					auth.token,
					currentProfile.id,
					currentProfile.disabled
				).then((response) => {
					if (response.data.errors) {
						// setAllProfiles([]);
						throw new Error(response.data.errors);
					} else {
						toast.info(
							currentProfile.disabled
								? 'Profile Enabled'
								: 'Profile Disabled',
							{ timeOut: 1000 }
						);
						refreshCurrentProfile();
					}
				});
			} catch (error) {
				console.error('Error in enabling or disabling profile', error);
				alert('There was an error when updating profile enabled state');
			}
		}
	};

	const enableDisableUser = () => {
		let auth = getToken();
		if (auth) {
			try {
				adminEnableOrDisableUser(
					auth.token,
					currentProfile.id,
					currentUser.disabled
				).then((response) => {
					if (response.data.errors) {
						// setAllProfiles([]);
						throw new Error(response.data.errors);
					} else {
						toast.info(
							currentUser.disabled
								? 'User Enabled'
								: 'User Disabled',
							{ timeOut: 1000 }
						);
						refreshCurrentProfile();
						// let currentEntities = [...filteredProfiles];
						// currentEntities.forEach((entity) => {
						// 	if (entity.id === userId) {
						// 		entity.profile.disabled =
						// 			!entity.profile.disabled;
						// 	}
						// });
						// setFilteredProfiles([...currentEntities]);
					}
				});
			} catch (error) {
				console.error('Error in enabling or disabling profile', error);
				alert('There was an error when updating profile enabled state');
			}
		}
	};

	const handleChange = (event) => {
		if (!editProfileEnabled) return;

		let { name, value } = event.target;
		if (name === 'isNri') {
			value = event.target.checked;
		}
		let profile = { ...currentProfile };
		profile[name] = value;
		setCurrentProfile(profile);
		!profileNeedsUpdate && setProfileNeedsUpdate(true);
	};

	const updatePhotos = async () => {
		try {
			let token = getToken()?.token;
			if (!token) {
				toast.error("Authentication error, please try again later");
				return;
			}

			// Get all uploaded image keys
			const uploadedKeys = profileImageUploadStatuses
				.map((status, index) => status === PROFILE_UPLOAD_STATUS_UPLOADED ? profileUploadDataKeys[index] : null)
				.filter(key => key !== null);

			if (uploadedKeys.length === 0) {
				toast.info("No images available to update");
				return;
			}

			const response = await adminMarkUploadedImageAsProfilePicture(token, uploadedKeys, id);
			if (response.errors) {
				toast.error("Failed to update profile images");
				return;
			}

			toast.success("Profile images updated successfully");
			refreshCurrentProfile(); // Refresh to get updated profile data
		} catch (error) {
			console.error("Error updating profile images:", error);
			toast.error("Failed to update profile images");
		}
	};
	return (
		<div style={{ padding: 20 }}>
			<ToastContainer />
			{!currentProfile ? (
				<div className='h-screen bg-white w-full flex items-center justify-center'>
					<span className="loading loading-dots loading-lg"></span>
				</div>
			) : (
				<>
					<Modal
						open={validityExtensionUser}
						onClose={() => setValidityExtensionUser(false)}
					>
						<Box sx={modalStyle}>
							<Typography
								id='modal-modal-description'
								sx={{ mt: 2 }}
							>
								Are you sure you want to extend the payment
								validity of user{' '}
								<b>
									{currentProfile.firstName +
										' ' +
										currentProfile.lastName}{' '}
								</b>{' '}
								by one year?
							</Typography>
							<Button
								fullWidth
								variant='contained'
								sx={{ mt: 2 }}
								onClick={extendValidity}
							>
								Extend Validity
							</Button>
						</Box>
					</Modal>
					<Grid container>
						<Grid item xs={12}>
							<h3>
								Profile Details - {currentProfile.firstName}{' '}
								{currentProfile.lastName}
							</h3>
						</Grid>
						<Grid item xs={12}>
							<div style={{ marginBottom: '8px' }}>
								<div style={{ fontWeight: 600, color: '#555', fontSize: '1rem', marginBottom: '2px' }}>Sign up Details</div>
								<div style={{ color: '#333', fontSize: '0.92em' }}>
									Phone: {currentUser.phone || <span style={{ color: '#aaa' }}>N/A</span>} &nbsp;|&nbsp; Email: {currentUser.email || <span style={{ color: '#aaa' }}>N/A</span>}
								</div>
							</div>
						</Grid>

						<Grid
							item
							xs={6}
							style={{ marginTop: 20, marginBottom: 20 }}
						>
							<h5>Profile Validity</h5>
							<div style={{ display: 'block' }}>
								Current Validity :{' '}
								{currentValidity(
									currentUser.remainingValidityMillis
								)}
							</div>
							<Grid container spacing={1} direction={{ xs: 'column', sm: 'row' }} alignItems="flex-start" style={{ marginTop: 8 }}>
								<Grid item>
									<Button
										variant='contained'
										onClick={() => setValidityExtensionUser(true)}
										disabled={
											!(
												parseFloat(
													currentUser.remainingValidityMillis
												) < 0
											)
										}
									>
										EXTEND VALIDITY
									</Button>
								</Grid>
							</Grid>
						</Grid>

						<Grid
							item
							xs={6}
							style={{ marginTop: 20, marginBottom: 20 }}
						>
							<h5>Access Control</h5>
							<Grid container spacing={1} direction={{ xs: 'column', sm: 'row' }} alignItems="flex-start" style={{ marginTop: 8 }}>
								<Grid item>
									<ThemeProvider theme={theme}>
										<Button
											color={
												currentProfile.disabled
													? 'green'
													: 'red'
											}
											variant='contained'
											onClick={enableDisableProfile}
										>
											{currentProfile.disabled
												? 'ENABLE PROFILE'
												: 'DISABLE PROFILE'}
										</Button>
									</ThemeProvider>
								</Grid>
								<Grid item>
									<ThemeProvider theme={theme}>
										<Button
											color={
												currentUser.disabled
													? 'green'
													: 'red'
											}
											variant='contained'
											onClick={enableDisableUser}
										>
											{currentUser.disabled
												? 'ENABLE USER'
												: 'DISABLE USER'}
										</Button>
									</ThemeProvider>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<Grid container spacing={1} direction={{ xs: 'column', sm: 'row' }} alignItems="flex-start" style={{ marginTop: 8, marginBottom: 8 }}>
								<Grid item>
									<ThemeProvider theme={theme}>
										<Button
											variant='contained'
											color={editProfileEnabled ? 'red' : 'green'}
											onClick={() =>
												setEditProfileEnabled(
													!editProfileEnabled
												)
											}
										>
											{editProfileEnabled
												? 'DISABLE EDIT'
												: 'ENABLE EDIT'}
										</Button>
									</ThemeProvider>
								</Grid>
								<Grid item>
									<Button
										style={{ marginLeft: 0 }}
										variant='contained'
										onClick={saveUserPofile}
										disabled={!profileNeedsUpdate}
									>
										SAVE PROFILE
									</Button>
								</Grid>
								<Grid item>
									<Button
										style={{ marginLeft: 0 }}
										variant='contained'
										onClick={() => {
											navigate(
												`/administratorpath/resetpassword/${currentProfile.id}?firstName=${currentProfile.firstName}&lastName=${currentProfile.lastName}`
											);
										}}
									>
										UPDATE PASSWORD
									</Button>
								</Grid>
							</Grid>
						</Grid>

						<Grid container spacing={3} xs={12} pt={2}>
							<Grid item xs={12}>
								<h5>Profile Images</h5>
							</Grid>
							<Grid item xs={12}>
								<div style={{ width: '200px', height: '267px' }}>
									<ProfileImage profile={currentProfile} refreshCount={refreshCount} />
								</div>
								<Typography variant="body1" sx={{ mt: 2 }}>
									Images Available: {currentProfile.isImageAvailable ? "Yes" : "No"}
								</Typography>
							</Grid>
						</Grid>

						{/* Image Uploader Section */}
						<Grid container spacing={3} xs={12} pt={2}>
							<Grid item xs={12}>
								<h5>Update Profile Images</h5>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
									Upload new images or replace existing ones
								</Typography>
							</Grid>
							<Grid item xs={12}>
								<ImageUploader
									fetchImageUploadData={fetchProfileUploadData}
									profileImageUploadUrls={profileImageUploadUrls}
									setProfileImageUploadStatuses={setProfileImageUploadStatuses}
									localProfileImageUrls={localProfileImageUrls}
									setLocalProfileImageUrls={setLocalProfileImageUrls}
									profileImageUploadStatuses={profileImageUploadStatuses}
									setProfileImageUploadUrls={setProfileImageUploadUrls}
									profileUploadDataKeys={profileUploadDataKeys}
									setProfileUploadDataKeys={setProfileUploadDataKeys}
								/>
							</Grid>
							<Grid item xs={12}>
								<Button
									variant="contained"
									color="primary"
									onClick={updatePhotos}
									sx={{ mt: 2 }}
								>
									Update Photos
								</Button>
							</Grid>
						</Grid>
						{/* Personal Details */}
						<Grid container spacing={3} xs={12} pt={2}>
							<Grid item xs={12}>
								<h5>Personal Details</h5>
							</Grid>
							<Grid item xs={6}>
								<TextField
									label={'Firstname'}
									name={'firstName'}
									fullWidth
									variant='standard'
									value={currentProfile.firstName}
									placeholder={'First Name'}
									onChange={handleChange}
									disabled={!editProfileEnabled}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									label={'Lastname'}
									name={'lastName'}
									fullWidth
									variant='standard'
									value={currentProfile.lastName}
									placeholder={'Last Name'}
									onChange={handleChange}
									disabled={!editProfileEnabled}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									label={'City'}
									name={'city'}
									fullWidth
									variant='standard'
									value={currentProfile.city}
									placeholder={'City'}
									onChange={handleChange}
									disabled={!editProfileEnabled}
								/>
							</Grid>
							<Grid item xs={6}>
								<SelectInput
									label={'State'}
									name={'state'}
									value={currentProfile.state}
									handleChange={handleChange}
									fullWidth
									variant='standard'
									placeholder='Select your State'
									options={stateOptions}
								/>
							</Grid>
							<Grid item xs={6}>
								<SelectInput
									label={'Country'}
									name={'country'}
									value={currentProfile.country}
									handleChange={handleChange}
									fullWidth
									variant='standard'
									placeholder='Select your Country'
									options={countryOptions}
								/>
							</Grid>
						</Grid>
						{/* About Details */}
						<Grid container spacing={3} xs={12} pt={2}>
							<Grid item xs={12}>
								<h5>About Details</h5>
							</Grid>
							<Grid item xs={12}>
								<TextField
									label={'Bio'}
									name={'bio'}
									fullWidth
									multiline
									variant='standard'
									value={currentProfile.bio}
									placeholder={'Bio'}
									onChange={handleChange}
									disabled={!editProfileEnabled}
								/>
							</Grid>
							<Grid item xs={12}>
								<TextField
									label={'Partner Preferences'}
									name={'userPreferences'}
									fullWidth
									multiline
									variant='standard'
									value={currentProfile.userPreferences}
									placeholder={'Describe what you are looking for in your partner'}
									onChange={handleChange}
									disabled={!editProfileEnabled}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									label={'Contact Number'}
									name={'contactNumber'}
									fullWidth
									multiline
									variant='standard'
									value={currentProfile.contactNumber}
									placeholder={'Contact Number'}
									onChange={handleChange}
									disabled={!editProfileEnabled}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									label={'Contact Email'}
									name={'contactEmail'}
									fullWidth
									multiline
									variant='standard'
									value={currentProfile.contactEmail}
									placeholder={'Contact Email'}
									onChange={handleChange}
									disabled={!editProfileEnabled}
								/>
							</Grid>
						</Grid>
						{/* Career Details */}
						<Grid container spacing={3} xs={12} pt={2}>
							<Grid item xs={12}>
								<h5>Career Details</h5>
							</Grid>
							<Grid item xs={6}>
								<SearchableSelectInput
									name='occupation'
									label='Occupation'
									value={currentProfile.occupation}
									handleChange={handleChange}
									placeholder='Select the Occupation'
									options={occupationOptions}
								/>
							</Grid>
							<Grid item xs={6}>
								<SelectInput
									name='annualIncome'
									label='Annual Income'
									value={currentProfile.annualIncome}
									handleChange={handleChange}
									fullWidth
									variant='standard'
									placeholder='Select the Annual Income'
									options={annualIncomeOptions}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									label={'Employed In'}
									name={'employedIn'}
									fullWidth
									variant='standard'
									value={currentProfile.employedIn}
									placeholder={
										'Enter your Organization/Company'
									}
									onChange={handleChange}
									disabled={!editProfileEnabled}
								/>
							</Grid>
							<Grid item xs={6}>
								<Typography>Are you a NRI ?</Typography>
								<Stack
									direction='row'
									spacing={1}
									alignItems='center'
								>
									<Typography>No</Typography>
									<FormControlLabel
										control={
											<Android12Switch
												name='isNri'
												checked={currentProfile.isNri}
												onChange={handleChange}
												disabled={!editProfileEnabled}
											/>
										}
									/>

									<Typography>Yes</Typography>
								</Stack>
							</Grid>
							<Grid
								item
								xs={12}
								sm={6}
								className='createprofile-qualification'
							>
								<MultiselectInputChipbox
									name='qualifications'
									label='Qualification'
									value={currentProfile.qualifications}
									onChange={handleChange}
									fullWidth
									variant='standard'
									placeholder='Select your Qualification'
									options={qualificationOptions}
								/>
							</Grid>
						</Grid>
						{/* Social Details */}
						<Grid container spacing={3} xs={12} pt={2}>
							<Grid item xs={12}>
								<h5>Social Details</h5>
							</Grid>
							<Grid item xs={6}>
								<SelectInput
									name='maritalStatus'
									label='Marital Status'
									value={currentProfile.maritalStatus}
									handleChange={handleChange}
									options={maritalStatusOptions}
								/>
							</Grid>
							<Grid item xs={6}>
								<SelectInput
									name='motherTongue'
									label='Mother Tongue'
									value={currentProfile.motherTongue}
									handleChange={handleChange}
									options={motherTongueOptions()}
								/>
							</Grid>
							<Grid item xs={6}>
								<SelectInput
									name='caste'
									label='Caste'
									value={currentProfile.caste}
									handleChange={handleChange}
									options={casteOptions}
								/>
							</Grid>
							<Grid item xs={6}>
								<SelectInput
									name='gotra'
									label='Gotra'
									value={currentProfile.gotra}
									handleChange={handleChange}
									options={gotraOptions}
								/>
							</Grid>
							<Grid item xs={6}>
								<SelectInput
									name='kuldevta'
									label='Kuldevta'
									value={currentProfile.kuldevta}
									handleChange={handleChange}
									options={kuldevataOptions}
								/>
							</Grid>
							<Grid item xs={6}>
								<SelectInput
									name='nakshatra'
									label='Nakshatra'
									value={currentProfile.nakshatra}
									handleChange={handleChange}
									options={nakshatraOptions}
								/>
							</Grid>
							<Grid item xs={6}>
								<SelectInput
									name='rashi'
									label='Rashi'
									value={currentProfile.rashi}
									handleChange={handleChange}
									options={rashiHindiOptions}
								/>
							</Grid>
							<Grid item xs={6}>
								<SelectInput
									name='math'
									label='Math'
									value={currentProfile.math}
									handleChange={handleChange}
									fullWidth
									variant='standard'
									placeholder='Select your Math'
									options={mathOptions}
								/>
							</Grid>
							<Grid item xs={6}>
								<MultiselectInputChipbox
									name='languagesKnown'
									label='Languages Known'
									value={currentProfile.languagesKnown}
									onChange={handleChange}
									fullWidth
									variant='standard'
									placeholder='Select your Language'
									options={languageOptions}
								/>
							</Grid>
						</Grid>

						<Grid container spacing={3} xs={12} pt={2}>
							<Grid item xs={12}>
								<h5>Other Details</h5>
							</Grid>
							<Grid item xs={6}>
								<SelectInput
									name='bloodGroup'
									label='Blood Group'
									value={currentProfile.bloodGroup}
									handleChange={handleChange}
									options={bloodGroupOptions}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									name='height'
									label='Height'
									value={currentProfile.height}
									type='number'
									variant='standard'
									placeholder='Height in cm'
									onChange={handleChange}
									fullWidth
									disabled={!editProfileEnabled}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									name='weight'
									label='Weight'
									value={currentProfile.weight}
									type='number'
									variant='standard'
									placeholder='Weight in kg'
									onChange={handleChange}
									fullWidth
									disabled={!editProfileEnabled}
								/>
							</Grid>
							<Grid item xs={6}>
								<SelectInput
									name='foodHabits'
									label='Food Habits'
									value={currentProfile.foodHabits}
									handleChange={handleChange}
									options={foodHabitsOptions}
								/>
							</Grid>
							<Grid item xs={6}>
								<SelectInput
									name='drinkingHabits'
									label='Drinking Habits'
									value={currentProfile.drinkingHabits}
									handleChange={handleChange}
									options={drinkingHabitsOptions}
								/>
							</Grid>
							<Grid item xs={6}>
								<SelectInput
									name='smokingHabits'
									label='Smoking Habits'
									value={currentProfile.smokingHabits}
									handleChange={handleChange}
									options={smokingHabitsOptions}
								/>
							</Grid>
						</Grid>

						<Grid container spacing={3} xs={12} pt={2}>
							<Grid item xs={12}>
								<h5>Family Details</h5>
							</Grid>
							<Grid item xs={6}>
								<TextField
									variant='standard'
									label='Father Name'
									fullWidth
									name='fatherName'
									value={currentProfile.fatherName}
									placeholder="Enter the father's name"
									onChange={handleChange}
									disabled={!editProfileEnabled}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									variant='standard'
									label='Father Number'
									fullWidth
									required
									name='fatherNumber'
									value={currentProfile.fatherNumber}
									placeholder="Enter the father's number"
									onChange={handleChange}
									disabled={!editProfileEnabled}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									variant='standard'
									label='Mother Name'
									fullWidth
									required
									name='motherName'
									value={currentProfile.motherName}
									placeholder="Enter the mother's name"
									onChange={handleChange}
									disabled={!editProfileEnabled}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									variant='standard'
									label='Mother Number'
									fullWidth
									required
									name='motherNumber'
									value={currentProfile.motherNumber}
									placeholder="Enter the mother's number"
									onChange={handleChange}
									disabled={!editProfileEnabled}
								/>
							</Grid>
						</Grid>
					</Grid>
				</>
			)}
		</div>
	);
};
